import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import cn from 'classnames';

import {addMaterialAction, removeMaterialAction} from 'redux/content/contentAsyncActions';
import {IArticleCard} from 'redux/content/interfaces';

import {useLoadFile} from 'utils/hooks/useLoadFile';

import Book from 'assets/image/library/book.svg';
import Clock from 'assets/image/library/clock.svg';
import {ReactComponent as CheckImg} from 'assets/image/library/ok-circle.svg';

import MaterialButton from './material-button';

import './styles.scss';

type Props = {
  card: IArticleCard;
  active?: boolean;
  checked?: boolean;
  disabledButtons?: boolean;
  showMaterialsButton?: boolean;
  isNew?: boolean;
  isPlaying?: boolean;
  onClick: () => void;
}

export const ArticleCard = ({
  card,
  active = false,
  checked = false,
  disabledButtons = false,
  showMaterialsButton,
  isNew = false,
  isPlaying = false,
  onClick
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const {
    media,
    getFileUrl
  } = useLoadFile();
  const [cardData, setCardData] = useState<IArticleCard>(card);
  const [isSave, setIsSave] = useState(card.material);

  useEffect(() => {
    const newCard = Object.assign({}, card);

    if (newCard.tags) {
      newCard.tags = newCard.tags.replace(/,/g, ', ');
    }

    if (!cardData.pictureUrl && cardData.picture) {
      newCard.pictureUrl = getFileUrl(cardData.picture);
    }

    setCardData(newCard);
  }, [card]);

  useEffect(() => {
    if (!cardData.pictureUrl && cardData.picture && media[cardData.picture]) {
      cardData.pictureUrl = media[cardData.picture];
      setCardData(cardData);
    }
  }, [media]);
  useEffect(() => {
    setIsSave(card.material);
  }, [card.material]);

  function addToMaterials() {
    setIsSave(!isSave);
    if (!isSave) {
      dispatch(addMaterialAction(card.id));
    } else {
      dispatch(removeMaterialAction([card.id]));
    }
  }

  function renderActiveCard() {
    return <div className="article-area">
      <div className={cn('article-area__card-place', {'article-area__card-place--playing': isPlaying})}>
        <div className="article-area__title">
          {cardData.title}
        </div>
        <div className="article-area__content">
          <div className="article-area__active-card">
            {showMaterialsButton
              ? <MaterialButton material={isSave} activeCard addToMaterials={addToMaterials}/>
              : null}
          </div>
        </div>
      </div>
    </div>;
  }

  function renderCheckedCard() {
    return <div className="article-area">
      <div className="article-area__card-place">
        <button className="article-area__card" onClick={onClick}>
          <div className="article-area__title">
            {cardData.title}
          </div>
          <div className="article-area__content">
            <div className="article-area__active-card">
              <CheckImg className="article-area__check-img"/>
            </div>
          </div>
        </button>
      </div>
    </div>;
  }

  function renderBasicCard() {
    return <div className="article-area">
      <div className={cn('article-area__card-place',
        {'article-area__card-place--new': isNew},
        {'article-area__card-place--playing': isPlaying})}>
        {isNew && <span className="article-area__new-badge"/>}
        <button className="article-area__card" onClick={onClick}>
          <div className="article-area__title">
            {cardData.title}
          </div>
          <div className="article-area__content">
            <div className="article-area__card-picture">
              <img src={cardData.pictureUrl || Book} role="presentation" alt="" className="article-area__image"/>
            </div>
            <div className="article-area__information">
              <div className="article-area__description">
                {cardData.description}
              </div>
              <div className="article-area__category">
                {cardData.tags}
              </div>
            </div>
          </div>
        </button>
        <div className={cn('article-area__bottom',
          {'article-area__pro-bottom': showMaterialsButton},
          {'article-area__disabled-bottom': disabledButtons})}>
          {showMaterialsButton && <div className="article-area__add-btn">
            <MaterialButton material={isSave} disabled={disabledButtons} addToMaterials={addToMaterials}/>
          </div>}
          <ul className={cn('article-area__addition', {'article-area__pro-addition': showMaterialsButton})}>
            <li className="article-area__addition-item">
              <div
                className="article-area__item-img"
                style={{backgroundImage: `url('${cardData.labelPictureUrl}')`}}
                title={cardData.labelText}
              />
            </li>
            <li className="article-area__addition-item">
              <div
                className="article-area__item-img"
                style={{backgroundImage: `url('${cardData.readingImageUrl || Clock}')`}}
                title={cardData.readingName}
              />
            </li>
          </ul>
        </div>
      </div>
    </div>;
  }

  if (active) {
    return renderActiveCard();
  }
  if (checked) {
    return renderCheckedCard();
  }

  return renderBasicCard();
};
